import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './VehicleCard.css'

const VehicleCard = ({ vehicle }) => {
  const navigate = useNavigate()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const cardRef = useRef(null)

  // Intersection Observer per a lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      {
        rootMargin: '50px', // Començar a carregar 50px abans de ser visible
        threshold: 0.1,
      }
    )

    if (cardRef.current) {
      observer.observe(cardRef.current)
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current)
      }
    }
  }, [])

  const handleClick = () => {
    navigate(`/vehicle/${vehicle['vehicle_id(PK)']}`)
  }

  // Obtenir array d'imatges - compatible amb múltiples formats
  let images = []

  if (vehicle.images && Array.isArray(vehicle.images) && vehicle.images.length > 0) {
    // Format nou: array d'objectes {url, ordre}
    images = vehicle.images.map(img => (typeof img === 'string' ? img : img.url)).filter(url => url)
  } else if (vehicle.url_imatge) {
    // Format antic: una sola imatge
    images = [vehicle.url_imatge]
  }

  // Debug temporal - eliminar després
  if (vehicle['vehicle_id(PK)'] === 1) {
    console.log('Vehicle data:', vehicle)
    console.log('Images array:', images)
  }

  const hasMultipleImages = images.length > 1

  const handlePrevImage = e => {
    e.stopPropagation()
    if (hasMultipleImages && !isAnimating) {
      setIsAnimating(true)
      setImageLoaded(false) // Reiniciar per a la nova imatge
      setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1))
      setTimeout(() => setIsAnimating(false), 300)
    }
  }

  const handleNextImage = e => {
    e.stopPropagation()
    if (hasMultipleImages && !isAnimating) {
      setIsAnimating(true)
      setImageLoaded(false) // Reiniciar per a la nova imatge
      setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1))
      setTimeout(() => setIsAnimating(false), 300)
    }
  }

  const currentImage = images[currentImageIndex]

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  return (
    <div className='vehicle-card' onClick={handleClick} style={{ cursor: 'pointer' }} ref={cardRef}>
      <div className='vehicle-image-container'>
        {currentImage ? (
          <>
            {/* Skeleton loader mientras la imagen carga */}
            {!imageLoaded && isVisible && (
              <div className='image-skeleton'>
                <div className='skeleton-shimmer'></div>
              </div>
            )}

            {/* Imagen con lazy loading */}
            {isVisible && (
              <img
                src={currentImage}
                alt={vehicle.model_name || 'Vehicle'}
                className={`${isAnimating ? 'image-animating' : ''} ${imageLoaded ? 'image-loaded' : 'image-loading'}`}
                onLoad={handleImageLoad}
                loading='lazy'
              />
            )}

            {hasMultipleImages && (
              <>
                <button className={`image-nav-btn prev ${isAnimating ? 'btn-animating' : ''}`} onClick={handlePrevImage} aria-label='Imagen anterior'>
                  ‹
                </button>
                <button className={`image-nav-btn next ${isAnimating ? 'btn-animating' : ''}`} onClick={handleNextImage} aria-label='Imagen siguiente'>
                  ›
                </button>

                <div className='image-indicators'>
                  {images.map((_, index) => (
                    <span key={index} className={`indicator ${index === currentImageIndex ? 'active' : ''}`} />
                  ))}
                </div>

                <div className='image-counter'>
                  {currentImageIndex + 1}/{images.length}
                </div>
              </>
            )}
          </>
        ) : (
          <div className='placeholder-image'>{vehicle.brand_name && vehicle.model_name ? `${vehicle.brand_name} ${vehicle.model_name}` : 'Imatge del Vehicle'}</div>
        )}
      </div>
      <div className='vehicle-info'>
        <h3>{vehicle.brand_name && vehicle.model_name ? `${vehicle.brand_name} ${vehicle.model_name}` : 'Nombre del vehiculo'}</h3>
        <p>{vehicle.any ? `Año: ${vehicle.any}` : ''}</p>
        <p>{vehicle.preu ? `Precio: ${Math.round(vehicle.preu).toLocaleString('es-ES')}€` : ''}</p>
        <p>{vehicle.kilometratge ? `${vehicle.kilometratge.toLocaleString('es-ES')} km` : ''}</p>
        <p style={{ fontWeight: 'bold' }}>{vehicle.offer_type_name ? `Oferta: ${vehicle.offer_type_name}` : ''}</p>
      </div>
    </div>
  )
}

export default VehicleCard
